import type { SubSimulation } from 'contexts/SimulationContext';

export type ResidentialArea = {
  id: number;
  minElevation: string | number;
  maxElevation: string | number;
  coordinates?: unknown;
};

export const hasValidResidentialElevations = (area: ResidentialArea) =>
  String(area.minElevation ?? '').trim() !== '' &&
  String(area.maxElevation ?? '').trim() !== '' &&
  Number.isFinite(Number(area.minElevation)) &&
  Number.isFinite(Number(area.maxElevation)) &&
  Number(area.maxElevation) > Number(area.minElevation);

type ShadowSimulationState = {
  shadowEnabled: boolean;
  subSimulations: Array<{
    simType?: string;
    params?: Record<string, unknown>;
  }>;
};

const hasPolygon = (value: unknown) => {
  if (!Array.isArray(value) || value.length === 0) return false;
  const coordinates = Array.isArray(value[0]) ? value[0] : value;
  return coordinates.filter((coordinate) => coordinate && typeof coordinate === 'object').length >= 3;
};

export const isShadowVolumeConfigured = (params: Record<string, unknown> = {}) => {
  const hasResidentialArea =
    Array.isArray(params.residentialAreas) &&
    (params.residentialAreas as ResidentialArea[]).some((area) => hasPolygon(area.coordinates));
  const hasNormalBuildableArea = hasPolygon(params.normalBuildableArea);

  return hasResidentialArea && (params.shadowRule === 'BS' || hasNormalBuildableArea);
};

export const isShadowSimulationDefined = (simulation: ShadowSimulationState) =>
  !simulation.shadowEnabled ||
  simulation.subSimulations.some((subSimulation) => {
    if (subSimulation.simType !== 'SHADOW') return false;
    return isShadowVolumeConfigured(subSimulation.params);
  });

export const createNextResidentialArea = (areas: ResidentialArea[]): ResidentialArea => ({
  id: Math.max(0, ...areas.map((area) => area.id)) + 1,
  minElevation: 0,
  maxElevation: 10,
});

export const getResidentialAreas = (params: Record<string, unknown>): ResidentialArea[] =>
  Array.isArray(params.residentialAreas)
    ? (params.residentialAreas as ResidentialArea[])
    : [
        {
          id: 1,
          minElevation: Number(params.residentialAreaMinElevation ?? 0),
          maxElevation: Number(params.residentialAreaMaxElevation ?? 10),
        },
      ];

export const getShadowSimulationsForVolumes = (
  subSimulations: SubSimulation[],
  volumeCount: number,
  createDefaultShadow: (id: number) => SubSimulation
) => {
  const shadowSimulations = subSimulations.filter((subSimulation) => subSimulation.simType === 'SHADOW');
  const unusedShadowSimulations = [...shadowSimulations];
  let nextId = Math.max(-1, ...subSimulations.map((subSimulation) => subSimulation.id)) + 1;

  const simulationsByVolume = Array.from({ length: volumeCount }, (_, analysisAreaIndex) => {
    const exactMatchIndex = unusedShadowSimulations.findIndex(
      (subSimulation) => Number(subSimulation.params.analysisAreaIndex) === analysisAreaIndex
    );
    const legacyMatchIndex = unusedShadowSimulations.findIndex(
      (subSimulation) => subSimulation.params.analysisAreaIndex == null
    );
    const matchIndex = exactMatchIndex >= 0 ? exactMatchIndex : legacyMatchIndex;
    const existing = matchIndex >= 0 ? unusedShadowSimulations.splice(matchIndex, 1)[0] : undefined;
    let subSimulation = existing;
    if (!subSimulation) {
      subSimulation = createDefaultShadow(nextId++);
      subSimulation = { ...subSimulation, name: shadowSimulations[0]?.name ?? subSimulation.name };
    }

    return Number(subSimulation.params.analysisAreaIndex) === analysisAreaIndex
      ? subSimulation
      : { ...subSimulation, params: { ...subSimulation.params, analysisAreaIndex } };
  });

  return [...subSimulations.filter((subSimulation) => subSimulation.simType !== 'SHADOW'), ...simulationsByVolume];
};
